import {
  Button,
  Input,
  Typography,
  Spin,
  Steps,
  Tooltip,
  message,
  Image,
  theme,
  Tag,
  Divider,
} from "antd";
import {
  createSecret,
  createSource,
  getGithubAppInstallUrl,
  getSource,
  updateSecret,
  updateSource,
} from "pages/api-clients/integrations";
import React from "react";
import { useOrg } from "src/hooks/useOrg";
import {
  Source,
  SourceType,
} from "pages/api/[orgId]/integrations-client/definitions";
import { Terminology } from "./Terminology";
import { useColorScheme } from "src/hooks/useColorScheme";
import { Flex } from "src/components/Flex";
import { useApplicationContext } from "src/hooks/useApplication";
import { useQuery } from "react-query";

const { Title, Text } = Typography;

enum IntegrationState {
  fetching,
  none,
  creating,
  noSecret,
  creatingSecret,
  updatingSecret,
  active,
  savingGithub,
}

export function GithubIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [githubSource, setGithubSource] = React.useState<Source | null>(null);
  const [sources, setSources] = React.useState<Source[]>([]);
  const [state, setState] = React.useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [secret, setSecret] = React.useState("");
  const [kind, setKind] = React.useState("");
  const [authToken, setAuthToken] = React.useState("");
  const [scope, setScope] = React.useState({ orgs: [] } as Record<string, any>);
  const fetchedOnce = React.useRef(false);

  // React.useEffect(() => {
  //   if (!fetchedOnce.current) {
  //     return;
  //   }
  //   if (githubSource == null) {
  //     setState(IntegrationState.none);
  //   } else {
  //     setState(IntegrationState.active);
  //   }
  // }, [githubSource]);

  const transitionRef = React.useRef({ githubSource, orgId });
  transitionRef.current = { githubSource, orgId };
  React.useEffect(() => {
    const { githubSource, orgId } = transitionRef.current;
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getSource(orgId, SourceType.Github).then((payload) => {
          const sources = payload.data;
          // choice: if there is an active token source, we will only consider that one.
          const activeTokenSource = sources.find(
            (source) => source.kind != "app" && source.status == "active"
          );
          if (activeTokenSource) {
            setGithubSource(activeTokenSource);
            setAuthToken(activeTokenSource.authToken || "");
            setSecret(activeTokenSource.secrets[0]?.secret || "");
            setScope(activeTokenSource.scope);
            setKind(activeTokenSource.kind);
            setState(IntegrationState.active);
          } else {
            setSources(sources.filter((s) => s.kind == "app"));
            setKind("app");
            setState(IntegrationState.active);
          }
          fetchedOnce.current = true;
        });
        break;
      }
      case IntegrationState.creating: {
        createSource(orgId, SourceType.Github).then((payload) => {
          setGithubSource(payload.data);
        });
        break;
      }
      case IntegrationState.creatingSecret: {
        createSecret(orgId, SourceType.Github, githubSource!.id, secret)
          .then((success) => {
            if (success) {
              message.success("Secret saved successfully");
            } else {
              message.error("Unable to save secret");
            }
          })
          .catch(() => {
            message.error("Unable to save secret");
          });
        break;
      }
      case IntegrationState.updatingSecret: {
        updateSecret(
          orgId,
          SourceType.Github,
          githubSource!.id,
          githubSource!.secrets[0].id,
          secret
        )
          .then((success) => {
            if (success) {
              message.success("Successfully updated secret");
            } else {
              message.error("Unable to update secret");
            }
            setState(IntegrationState.fetching);
          })
          .catch(() => {
            message.error("Unable to update secret");
            setState(IntegrationState.fetching);
          });
        break;
      }
      case IntegrationState.savingGithub: {
        updateSource(orgId, SourceType.Github, {
          id: githubSource!.id,
          authToken,
          scope,
        }).then((payload) => {
          setGithubSource(payload.data);
          setState(IntegrationState.fetching);
          message.success("Successfully updated github integration");
        });
        break;
      }
    }
  }, [state, authToken, scope, secret]);

  return [
    IntegrationState.fetching,
    IntegrationState.creating,
    IntegrationState.creatingSecret,
    IntegrationState.updatingSecret,
    IntegrationState.savingGithub,
  ].includes(state) ? (
    <Spin />
  ) : state == IntegrationState.none && !kind ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create Github Integration
    </Button>
  ) : kind == "app" ? (
    <AppConfig sources={sources} />
  ) : (
    <ManualConfig
      githubSource={githubSource}
      setState={setState}
      authToken={authToken}
      setAuthToken={setAuthToken}
      secret={secret}
      setSecret={setSecret}
      scope={scope}
      setScope={setScope}
    />
  );
}

const AppConfig = (props: { sources: Source[] }) => {
  const orgId = useApplicationContext().org_id;
  const installData = useQuery(
    ["gh-app-install-url", { orgId }],
    async () => (orgId ? getGithubAppInstallUrl(orgId) : null),
    { enabled: Boolean(orgId) }
  );
  const installUrl = installData?.data?.data?.url;

  return (
    <>
      <Terminology>
        <p>
          This page will forward you to github to install the VZBL github app on
          your organizations.
        </p>
        <p>
          Install the app on each organization whose data you want to ingest.
          You may also install the app on your personal account. In this case,
          only the repositories that belong to your account will be ingested.
        </p>
      </Terminology>
      {props.sources.length > 0 ? (
        <>
          <Divider />

          <p>you have installed the vzbl github app on:</p>
        </>
      ) : (
        ""
      )}
      {props.sources.map((s) => (
        <p key={s.id}>
          <Tag>{s.installationAccount}</Tag>
        </p>
      ))}
      {installUrl ? (
        <p>
          <Divider />
          <p>
            {props.sources.length === 0 ? "Install" : "Manage"} the vzbl github
            app:
          </p>

          <Button href={installUrl} target="_blank" type="primary">
            {props.sources.length === 0
              ? "Install on github"
              : "Install or manage on github"}
          </Button>
        </p>
      ) : (
        <></>
      )}
    </>
  );

  // return installUrl
};

const ManualConfig = (props: {
  githubSource: Source | null;
  setState: (state: IntegrationState) => void;
  authToken: string;
  setAuthToken: (token: string) => void;
  secret: string;
  setSecret: (secret: string) => void;
  scope: Record<string, any>;
  setScope: (scope: Record<string, any>) => void;
}) => {
  const {
    githubSource,
    setState,
    authToken,
    setAuthToken,
    secret,
    setSecret,
    scope,
    setScope,
  } = props;

  const mode = useColorScheme();
  const { token } = theme.useToken();

  const listWebhookItems = [
    {
      description: (
        <p>
          Make sure you have your github workspace admin navigate to org
          settings -&gt; webhooks <br />
          <Image
            style={{
              filter: mode === "light" ? "invert()" : undefined,
              border: `solid 1px ${token.colorTextSecondary}`,
              borderRadius: 3,
            }}
            src="/instructions/github/org_settings.png"
            alt="org settings tab"
          />
        </p>
      ),
    },
    {
      description: (
        <div>
          Click “Add webhook”
          <ul>
            <li>
              For <strong>payload url</strong> use:
              <Text code copyable>
                {`https://int.vzbl.dev/api/v1/github/webhook/${githubSource?.uuid}`}
              </Text>
            </li>
            <li>
              For “Content Type” select <strong>application/json</strong>
            </li>
            <li>
              For “Secret” add a secret. Paste that secret below under “Webhook
              secret”, and click ”Save”
            </li>
            <li>Enable SSL verification</li>
            <li>
              Select “<strong>send me everything</strong>” for which events the
              trigger the webhook
            </li>
          </ul>
        </div>
      ),
    },
    { description: "Click ”Add Webhook” " },
  ];

  const listTokenItems = [
    {
      description: (
        <>
          Access github, then access your personal tokens:{" "}
          <a
            href="https://github.com/settings/tokens"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://github.com/settings/tokens
          </a>
        </>
      ),
    },
    {
      description: (
        <>
          <div style={{ float: "left" }}>
            Click ”Generate new Token” and pick ”classic”,
            <ul>
              <li>
                Enter a descriptive note (e.g. &quot;vzbl integration&quot;)
              </li>
              <li>Set &quot;no expiration date&quot;</li>
              <li>
                Select Scopes: &quot;repo&quot;, &quot;read:packages&quot;,
                &quot;read:org&quot;
              </li>
            </ul>
          </div>

          <Flex gap={16} style={{ float: "right" }}>
            <Image
              style={{
                filter: mode === "light" ? "invert()" : undefined,
                border: `solid 1px ${token.colorTextSecondary}`,
                borderRadius: 3,
              }}
              alt="Github: generate classic token"
              height={100}
              src="/instructions/github/token_navigation.png"
            />
            <Image
              style={{
                filter: mode === "light" ? "invert()" : undefined,
                border: `solid 1px ${token.colorTextSecondary}`,
                borderRadius: 3,
              }}
              alt="Token Creation"
              height={100}
              src="/instructions/github/token_creation.png"
            />
            <Image
              style={{
                filter: mode === "light" ? "invert()" : undefined,
                border: `solid 1px ${token.colorTextSecondary}`,
                borderRadius: 3,
              }}
              height={100}
              alt="Token Configuration"
              src="/instructions/github/token_configuration.png"
            />
          </Flex>
        </>
      ),
    },
    {
      description: (
        <ul>
          <li>Scroll down and click &quot;Generate Token&quot;</li>

          <li>
            Copy the token, paste it below under &quot;Auth token&quot;, then
            click &quot;save&quot;
          </li>
        </ul>
      ),
    },
  ];

  return (
    <div>
      <Terminology>
        <p>
          This page will guide you through setting up a webhook and/or personal
          auth token.
        </p>
        <p>
          <Tag bordered={false}>GitHub Webhook</Tag>
          <ul>
            <li>Follow steps to create and save a secret</li>
            <li>Used to obtain always up-to-date data</li>
          </ul>
          <Tag bordered={false}>Auth Token</Tag>
          <ul>
            <li>Follow steps to create and save a token string</li>
            <li>Optionally configure org access</li>
            <li>Used to obtain historical data</li>
          </ul>
        </p>
        <p>
          One may be enough to get started, but for best results set up both.
        </p>
      </Terminology>
      <Divider />

      <h3>
        <Tag bordered={false}>GitHub Webhook</Tag> configuration
      </h3>
      <div style={{ margin: "16px 40px" }}>
        <Steps
          direction="vertical"
          progressDot
          items={listWebhookItems}
          current={listWebhookItems.length - 1}
        />
      </div>
      <Tooltip
        placement="topLeft"
        title="This is extra security that guarantees 1. webhook data is originating from github 2. the data was not tampered with."
      >
        <Title level={5}>
          <Tag bordered={false}>GitHub Webhook</Tag>secret
        </Title>
        <Input.Password
          defaultValue={secret}
          visibilityToggle={secret != "placeholder"}
          allowClear
          style={{ width: "40%" }}
          onChange={(event) => setSecret(event.target.value)}
        />
        <Button
          type="primary"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            if (githubSource?.secrets.length === 0) {
              setState(IntegrationState.creatingSecret);
            } else {
              setState(IntegrationState.updatingSecret);
            }
          }}
          disabled={!secret}
        >
          Save
        </Button>
      </Tooltip>

      <Divider />
      <h3>
        <Tag bordered={false}>Auth Token</Tag> configuration
      </h3>

      <div style={{ margin: "16px 40px" }}>
        <Steps
          direction="vertical"
          progressDot
          items={listTokenItems}
          current={listTokenItems.length - 1}
        />
      </div>
      <p style={{ marginTop: 24, maxWidth: 700 }}>
        <Tooltip
          placement="topLeft"
          title="Enable more insights by letting VZBL pull your diffs and summarizing them"
        >
          <Title level={5}>
            <Tag bordered={false}>Auth Token</Tag> string
          </Title>
          <Input.Password
            allowClear
            visibilityToggle={authToken != "placeholder"}
            defaultValue={authToken}
            style={{ width: "80%" }}
            onChange={(event) => setAuthToken(event.target.value)}
          />
          <Button
            type="primary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setState(IntegrationState.savingGithub);
            }}
            disabled={!authToken}
          >
            Save
          </Button>
        </Tooltip>
      </p>

      <p style={{ marginTop: 24, maxWidth: 700 }}>
        Your token may have access to several organizations. If you wish to
        restrict which organizations to connect to vzbl, enter a comma-separated
        list of organizations then click &quot;save&quot;
        <Tooltip
          placement="topLeft"
          title="Comma-separated list of which github orgs to pull (necessary if your token has access to several orgs)"
        >
          <Title level={5}>Github orgs</Title>
          <Input
            allowClear
            defaultValue={scope?.orgs}
            style={{ width: "80%" }}
            onChange={(event) =>
              setScope({
                orgs: event.target.value.split(",").map((s) => s.trim()),
              })
            }
          />
          <Button
            type="primary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setState(IntegrationState.savingGithub);
            }}
            disabled={!authToken}
          >
            Save
          </Button>
        </Tooltip>
      </p>
    </div>
  );
};
